/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, queryFormEditable, State, TextTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  buildEditableTextColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { Observable } from 'rxjs';
import {
  getJobCsDetailsHeadingId,
  JobCsDetailsHeading,
  jobCsDetailsHeadingFieldState,
  validateJobCsDetailsHeadingField,
} from '../../../resources/job-cs-details.model';
import { JobCsDetailsCommandService } from '../job-cs-details-command.service';
import {
  JobCsDetailsState,
  queryJobCsFocusedHeadingCell,
  queryJobCsHeadings,
  queryJobCsSelectedHeadingId,
} from '../job-cs-details.state';

@Component({
  selector: 'gh-job-cs-details-heading-list',
  template: require('./job-cs-details-heading-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCsDetailsHeadingListComponent implements OnInit {
  headings$: Observable<JobCsDetailsHeading[]>;
  selectedId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;

  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private state: State<JobCsDetailsState>,
              private commands: JobCsDetailsCommandService,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();

    this.headings$ = this.state.select(queryJobCsHeadings);
    this.selectedId$ = this.state.select(queryJobCsSelectedHeadingId);
    this.focusedCell$ = this.state.select(queryJobCsFocusedHeadingCell);
  }

  onAdd(): void {
    this.commands.headingAdd();
  }

  onClone(heading: JobCsDetailsHeading): void {
    this.commands.headingClone(heading);
  }

  onRemove(heading: JobCsDetailsHeading): void {
    this.commands.headingRemove(heading);
  }

  onSelectedChanged(headingId: ID): void {
    this.commands.headingSelect(headingId);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  private moveHeadings(from: JobCsDetailsHeading, to: JobCsDetailsHeading): void {
    this.commands.headingMove(from, to);
  }

  private buildGridDefinition(): LayoutBasedGridDefinition {
    const { headingGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getJobCsDetailsHeadingId,
      validate: validateJobCsDetailsHeadingField,
      getCellDef: jobCsDetailsHeadingFieldState,
      command: (entity, name, value) => this.commands.setHeadingField(getJobCsDetailsHeadingId(entity), name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      options: <any>{
        rowSelection: 'single',
        defaultColDef: {
          sortable: false,
        },
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveHeadings.bind(this)),
        rowClassRules: { [ROW_DIRTY_CLASS]: ({ data }: { data: JobCsDetailsHeading }) => data.isDirty },
      },
      columns: {
        name: {
          ...buildEditableTextColumn(TextTypes.Text40, model.getCellAccessor('name')),
          rowDrag: this.editable,
        },
        isNewPage: buildEditableCheckboxColumn(model.getCellAccessor('isNewPage')),
      },
      model,
      layout: headingGridLayout,
    });
  }
}
